import {Log} from '@aboutyou/mlm';
import {
  useCurrentPageType,
  useCurrentPageTypeCallback,
  useHistory,
  useInitialLocation,
} from '@aboutyou/router';
import React, {useCallback, useEffect} from 'react';
import {LAST_VIEW_CHANGED_WEB_VITALS} from './constants';
import {getTransactionName} from './getTransactionName';

export const DatadogLogsRouteTracker: React.FC = () => {
  return (
    <>
      <LogsRouteTracker />
      <NotifyViewChangeForWebvitalsDeltaReporting />
    </>
  );
};

const NotifyViewChangeForWebvitalsDeltaReporting: React.FC = () => {
  const getCurrentPageType = useCurrentPageTypeCallback();
  const history = useHistory();

  const stalePageType = useCurrentPageType();
  const {pathname, search} = useInitialLocation();
  const lastUrl = React.useRef<string>(`${pathname}${search}`);
  const lastPageTypeViewName = getTransactionName(
    lastUrl.current,
    stalePageType,
  );

  const notifyLastPageTypeChange = useCallback(
    (location: Location) => {
      const url = `${location.pathname}${location.search}`;
      const newPageType = getCurrentPageType();
      const newPageTypeViewName = getTransactionName(url, newPageType);

      if (lastUrl.current !== url) {
        window.dispatchEvent(
          new CustomEvent(LAST_VIEW_CHANGED_WEB_VITALS, {
            detail: {
              lastPageType: lastPageTypeViewName,
              newPageType: newPageTypeViewName,
            },
          }),
        );
        lastUrl.current = url;
      }
    },
    [getCurrentPageType, lastPageTypeViewName],
  );

  // whenever history changes
  // 1. check if the url also changed (ignore state pushes, for eg: open-state-manager)
  // 2. if url changed, notify the last page type change
  useEffect(() => {
    return history.listen(location => {
      notifyLastPageTypeChange(location as unknown as Location);
    });
  }, [history, notifyLastPageTypeChange]);

  // when the Window is closed, notify current page as the last opened page
  useEffect(() => {
    const notifyLastView = () => {
      window.dispatchEvent(
        new CustomEvent(LAST_VIEW_CHANGED_WEB_VITALS, {
          detail: {lastPageType: lastPageTypeViewName, newPageType: null},
        }),
      );
    };
    window.addEventListener('beforeunload', notifyLastView);

    return () => {
      window.removeEventListener('beforeunload', notifyLastView);
    };
  }, [lastPageTypeViewName]);

  return null;
};

const LogsRouteTracker: React.FC = () => {
  const getCurrentPageType = useCurrentPageTypeCallback();
  const history = useHistory();

  const trackPageType = useCallback(
    (location: Location) => {
      const currentPageType = getCurrentPageType();
      const url = `${location.pathname}${location.search}`;
      const transactionName = getTransactionName(url, currentPageType);

      try {
        Log.setCustomAttribute('pageType', transactionName);
        Log.updateViewName(transactionName);
      } catch (e) {}
    },
    [getCurrentPageType],
  );

  useEffect(() => {
    return history.listen(location => {
      trackPageType(location as unknown as Location);
    });
  }, [history, trackPageType]);

  return null;
};
